import { useNavigate } from "react-router-dom";
import { BarChart3, CheckCircle, Shield, Smartphone, Database, Sliders, Link2, MessageCircle } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { API_HOST, getTelegramPhotoUrl } from "../constants";
import { removeTelegramUserFromStorage } from "../utils/authStorage";
import type { TelegramUser } from "../types";
import { UserContextSurvey, getUserContextDisplayRows } from "../components/UserContextSurvey";
import { AssessmentQuizModal } from "../components/assessment/AssessmentQuizModal";
import type { UserContextAnswers } from "../types";

interface ProfileProps {
  user: TelegramUser | null;
  onLogout: () => void;
}

export const Profile = ({ user, onLogout }: ProfileProps) => {

  const navigate = useNavigate();
  const [fullUser, setFullUser] = useState<TelegramUser | null>(user);
  const [photoError, setPhotoError] = useState(false);
  const [contextModalOpen, setContextModalOpen] = useState(false);
  const [quizModalOpen, setQuizModalOpen] = useState(false);
  const [linkTgToken, setLinkTgToken] = useState<string | null>(null);
  const [linkTgStatus, setLinkTgStatus] = useState<"idle" | "waiting" | "linked" | "expired">("idle");
  const [linkTgDeepLink, setLinkTgDeepLink] = useState<string | null>(null);
  const linkTgPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Используем данные пользователя из props (уже загружены при авторизации)
  // Загружаем с сервера только для обновления прогресса в фоне
  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    // Сначала используем данные из props (быстрое отображение)
    setFullUser(user);

    // Загружаем актуальный прогресс с сервера в фоне
    // Это нужно для синхронизации, если прогресс изменился на другом устройстве
    const fetchUser = async () => {
      try {
        const res = await fetch(`${API_HOST}/user/${user.id}`);
        if (res.ok) {
          const data = await res.json();
          // Обновляем данные пользователя, включая прогресс и контекст опросника
          setFullUser({
            ...user,
            ...data,
            progress: data.progress || user.progress || {},
            user_context: data.user_context ?? user.user_context,
            user_context_completed_at: data.user_context_completed_at ?? user.user_context_completed_at,
          });
        }
      } catch (err) {
        // Игнорируем ошибки - используем данные из props
        console.error("Не удалось обновить данные пользователя:", err);
      }
    };

    // Загружаем в фоне с небольшой задержкой, чтобы не блокировать рендер
    const timeoutId = setTimeout(fetchUser, 100);
    return () => clearTimeout(timeoutId);
  }, [user, navigate]);

  // Stop polling when component unmounts
  useEffect(() => {
    return () => {
      if (linkTgPollRef.current) clearInterval(linkTgPollRef.current);
    };
  }, []);

  const startLinkTelegram = async () => {
    if (!fullUser) return;
    try {
      const res = await fetch(`${API_HOST}/auth/link-telegram/generate-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: fullUser.id }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || "Ошибка генерации токена");
        return;
      }
      const data = await res.json();
      setLinkTgToken(data.token);
      setLinkTgDeepLink(data.deep_link);
      setLinkTgStatus("waiting");

      // Open bot deep link
      window.open(data.deep_link, "_blank");

      // Poll for confirmation
      if (linkTgPollRef.current) clearInterval(linkTgPollRef.current);
      linkTgPollRef.current = setInterval(async () => {
        try {
          const poll = await fetch(`${API_HOST}/auth/link-telegram/status/${data.token}`);
          if (!poll.ok) return;
          const status = await poll.json();
          if (status.status === "linked") {
            clearInterval(linkTgPollRef.current!);
            linkTgPollRef.current = null;
            setLinkTgStatus("linked");
            // Refresh user data
            const userRes = await fetch(`${API_HOST}/user/${fullUser.id}`);
            if (userRes.ok) {
              const userData = await userRes.json();
              setFullUser((prev) => prev ? { ...prev, ...userData } : prev);
            }
          } else if (status.status === "expired") {
            clearInterval(linkTgPollRef.current!);
            linkTgPollRef.current = null;
            setLinkTgStatus("expired");
          }
        } catch {
          // ignore poll errors
        }
      }, 2000);
    } catch (err) {
      console.error(err);
      alert("Не удалось начать привязку Telegram");
    }
  };

  // Формируем URL к прокси-эндпоинту на основе file_path
  // Важно: вычисляем ДО раннего return, чтобы порядок хуков был стабилен
  const displayName = fullUser?.first_name || (fullUser as any)?.widget_first_name || "Пользователь";
  const photoFilePath = (fullUser as any)?.photo_url || (fullUser as any)?.widget_photo_url;
  const displayPhoto = getTelegramPhotoUrl(photoFilePath);

  // Сбрасываем ошибку при изменении фото
  useEffect(() => {
    setPhotoError(false);
  }, [displayPhoto]);

  if (!fullUser) return null;

  const contextRows = getUserContextDisplayRows(fullUser.user_context);

  const benefits = [
    { icon: <Shield className="text-primary" size={18} />, text: "Прогресс сохраняется автоматически" },
    { icon: <Smartphone className="text-accent-foreground" size={18} />, text: "Доступ с любого устройства" },
    { icon: <Database className="text-primary" size={18} />, text: "Все решения и история задач" },
    { icon: <CheckCircle className="text-accent-foreground" size={18} />, text: "Персональный AI Tutor" },
  ];

  return (
    <div className="min-h-[calc(100vh-6rem)] bg-gradient-to-b from-neutral-light to-neutral-bg p-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Заголовок страницы */}
        <div className="mb-8 text-center animate-fadeIn">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-neutral-text mb-2">
            Профиль
          </h1>
          <p className="text-neutral-text/70 font-body">
            Прогресс, настройки AI Tutor и управление аккаунтом
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Левая колонка - Профиль пользователя */}
          <div className="md:col-span-1">
            <div className="bg-neutral-bg rounded-2xl shadow-md p-6 text-center space-y-4 animate-fadeIn border border-neutral-border">
              {/* Фото и имя */}
              <div className="space-y-3">
                {displayPhoto && !photoError ? (
                  <img
                    src={displayPhoto}
                    alt={`Фото профиля ${displayName}`}
                    className="w-20 h-20 mx-auto rounded-full border-2 border-primary shadow-md"
                    width="80"
                    height="80"
                    loading="lazy"
                    decoding="async"
                    onError={() => setPhotoError(true)}
                  />
                ) : (
                  <div className="w-20 h-20 mx-auto rounded-full bg-primary flex items-center justify-center text-white text-3xl font-bold shadow-md">
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-heading font-semibold text-neutral-text">
                    {displayName}
                  </h2>
                  {fullUser.username && (
                    <p className="text-neutral-text/70 text-sm font-body">@{fullUser.username}</p>
                  )}
                  <p className="text-neutral-text/40 text-xs font-body mt-1">
                    {fullUser.telegram_id ? "Telegram" : fullUser.yandex_id ? "Яндекс ID" : ""}
                  </p>
                </div>
              </div>

              {/* Действия */}
              <div className="pt-4 border-t border-neutral-border space-y-2">
                <button
                  onClick={() => navigate("/simulator")}
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition flex items-center justify-center gap-2 font-medium shadow-sm"
                >
                  <BarChart3 size={18} />
                  К задачам
                </button>

                <button
                  onClick={() => {
                    removeTelegramUserFromStorage();
                    onLogout();
                  }}
                  className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-medium"
                >
                  Выйти
                </button>
              </div>
            </div>
          </div>

          {/* Правая колонка - Статистика и прогресс */}
          <div className="md:col-span-2 space-y-6">
            {/* Контекст для AI Tutor */}
            <div className="bg-neutral-bg rounded-2xl shadow-md p-6 animate-fadeIn border border-neutral-border">
              <h3 className="text-xl font-heading font-semibold text-neutral-text mb-4 flex items-center gap-2">
                <Sliders className="w-5 h-5 text-primary" />
                О себе для AI Tutor
              </h3>
              <p className="text-sm text-neutral-text/70 mb-4">
                Эти ответы помогают тьютору подстраивать объяснения под ваш опыт и цели.
              </p>
              {contextRows.length > 0 ? (
                <ul className="space-y-2 mb-4">
                  {contextRows.map((row, i) => (
                    <li key={i} className="text-sm">
                      <span className="text-neutral-text/70">{row.question}: </span>
                      <span className="text-neutral-text font-medium">{row.answer}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-neutral-text/60 mb-4">Опросник пока не заполнен.</p>
              )}
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setContextModalOpen(true)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-primary text-primary hover:bg-primary/10 transition"
                >
                  {contextRows.length > 0 ? "Изменить" : "Заполнить"}
                </button>
                <button
                  type="button"
                  onClick={() => setQuizModalOpen(true)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-neutral-border text-neutral-text hover:bg-neutral-light transition"
                >
                  Пройти полный опрос
                </button>
              </div>
            </div>

            {/* Привязка Telegram (только для Яндекс-пользователей без telegram_id) */}
            {fullUser.yandex_id && !fullUser.telegram_id && (
              <div className="bg-neutral-bg rounded-2xl shadow-md p-6 animate-fadeIn border border-neutral-border">
                <h3 className="text-xl font-heading font-semibold text-neutral-text mb-2 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-blue-500" />
                  Привязать Telegram
                </h3>
                <p className="text-sm text-neutral-text/70 mb-4">
                  Привяжите Telegram-аккаунт, чтобы получать уведомления об обновлениях и входить через бота.
                </p>
                {linkTgStatus === "idle" && (
                  <button
                    type="button"
                    onClick={startLinkTelegram}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition flex items-center gap-2"
                  >
                    <Link2 size={16} />
                    Привязать через Telegram
                  </button>
                )}
                {linkTgStatus === "waiting" && (
                  <div className="space-y-3">
                    <p className="text-sm text-neutral-text/80">
                      Откройте бота и отправьте <code className="bg-neutral-light px-1 rounded">/start</code> — привязка произойдёт автоматически.
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                      <span className="text-sm text-neutral-text/60">Ожидаем подтверждения...</span>
                    </div>
                    {linkTgDeepLink && (
                      <a
                        href={linkTgDeepLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-500 hover:underline"
                      >
                        Открыть Telegram ещё раз
                      </a>
                    )}
                  </div>
                )}
                {linkTgStatus === "linked" && (
                  <p className="text-sm text-green-600 font-medium flex items-center gap-2">
                    <CheckCircle size={16} />
                    Telegram успешно привязан!
                  </p>
                )}
                {linkTgStatus === "expired" && (
                  <div className="space-y-2">
                    <p className="text-sm text-red-500">Ссылка истекла. Попробуйте ещё раз.</p>
                    <button
                      type="button"
                      onClick={() => { setLinkTgStatus("idle"); setLinkTgToken(null); }}
                      className="px-3 py-1.5 text-sm rounded-lg border border-neutral-border hover:bg-neutral-light transition"
                    >
                      Попробовать снова
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Возможности */}
            <div className="bg-neutral-bg rounded-2xl shadow-md p-6 animate-fadeIn border border-neutral-border">
              <h3 className="text-xl font-heading font-semibold text-neutral-text mb-4">
                Ваши возможности
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((b, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-3 bg-neutral-light rounded-lg border border-neutral-border hover:border-primary/30 transition-colors"
                  >
                    <div className="flex-shrink-0 mt-0.5">{b.icon}</div>
                    <span className="text-sm text-neutral-text font-body">{b.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {contextModalOpen && fullUser && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setContextModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Настройки контекста для AI Tutor"
        >
          <div
            className="w-full max-w-md max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <UserContextSurvey
              userId={fullUser.id}
              initialAnswers={
                typeof fullUser.user_context === "string"
                  ? (() => {
                      try {
                        return JSON.parse(fullUser.user_context) as UserContextAnswers;
                      } catch {
                        return null;
                      }
                    })()
                  : (fullUser.user_context as UserContextAnswers | undefined) ?? null
              }
              onSave={() => {
                fetch(`${API_HOST}/user/${fullUser.id}`)
                  .then((r) => r.ok && r.json())
                  .then((data) => {
                    if (data) setFullUser((prev) => (prev ? { ...prev, ...data } : prev));
                  })
                  .catch(() => {});
              }}
              onClose={() => setContextModalOpen(false)}
            />
          </div>
        </div>,
        document.body
      )}

      {quizModalOpen && fullUser && createPortal(
        <AssessmentQuizModal
          userId={fullUser.id}
          onClose={() => {
            setQuizModalOpen(false);
            // Refresh user data to get updated user_context
            fetch(`${API_HOST}/user/${fullUser.id}`)
              .then((r) => r.ok && r.json())
              .then((data) => {
                if (data) setFullUser((prev) => (prev ? { ...prev, ...data } : prev));
              })
              .catch(() => {});
          }}
          onComplete={() => {
            // Refresh user data after quiz completion
            fetch(`${API_HOST}/user/${fullUser.id}`)
              .then((r) => r.ok && r.json())
              .then((data) => {
                if (data) setFullUser((prev) => (prev ? { ...prev, ...data } : prev));
              })
              .catch(() => {});
          }}
        />,
        document.body
      )}

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }

          @keyframes tooltipFade {
            from { opacity: 0; transform: translateY(-6px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-tooltip { animation: tooltipFade 0.3s ease-out forwards; opacity: 1; }
        `}
      </style>
    </div>
  );
};
